/**
 * dt-input - Text input with validation display
 * Supports label, error, helper text, and multiple input types
 */
import { BaseElement } from '../base-element';

export class DtInput extends BaseElement {
  static observedAttributes = [
    'label',
    'placeholder',
    'value',
    'type',
    'error',
    'helper-text',
    'required',
    'disabled',
  ];

  private _label = '';
  private _placeholder = '';
  private _value = '';
  private _type: 'text' | 'email' | 'tel' | 'date' = 'text';
  private _error: string | null = null;
  private _helperText: string | null = null;
  private _required = false;
  private _disabled = false;
  private _touched = false;
  private _validationMessage: string | null = null;
  private _inputId = `dt-input-${Math.random().toString(36).slice(2, 9)}`;

  get label(): string {
    return this._label;
  }
  set label(value: string) {
    this._label = value;
    this.updateRender();
  }

  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.updateRender();
  }

  get value(): string {
    return this._value;
  }
  set value(val: string) {
    this._value = val;
    const input = this.$('input') as HTMLInputElement | null;
    if (input && input.value !== val) {
      input.value = val;
    }
  }

  get type(): string {
    return this._type;
  }
  set type(value: string) {
    if (['text', 'email', 'tel', 'date'].includes(value)) {
      this._type = value as 'text' | 'email' | 'tel' | 'date';
      this.updateRender();
    }
  }

  get error(): string | null {
    return this._error;
  }
  set error(value: string | null) {
    this._error = value;
    this.updateRender();
  }

  get helperText(): string | null {
    return this._helperText;
  }
  set helperText(value: string | null) {
    this._helperText = value;
    this.updateRender();
  }

  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = value;
    this.updateRender();
  }

  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value;
    this.updateRender();
  }

  private _validate(): boolean {
    if (this._required && this._touched && !this._value.trim()) {
      this._validationMessage = 'This field is required';
    } else {
      this._validationMessage = null;
    }
    return !this._validationMessage;
  }

  validate(): boolean {
    this._touched = true;
    const result = this._validate();
    this.updateRender();
    return result && !this._error;
  }

  connectedCallback(): void {
    this.updateRender();
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'label':
        this._label = newValue || '';
        break;
      case 'placeholder':
        this._placeholder = newValue || '';
        break;
      case 'value':
        this._value = newValue || '';
        break;
      case 'type':
        this.type = newValue;
        return; // type setter handles render
      case 'error':
        this._error = newValue;
        break;
      case 'helper-text':
        this._helperText = newValue;
        break;
      case 'required':
        this._required = newValue !== null && newValue !== 'false';
        break;
      case 'disabled':
        this._disabled = newValue !== null && newValue !== 'false';
        break;
    }
    this.updateRender();
  }

  private handleInput = (e: Event): void => {
    e.stopPropagation();
    const target = e.target as HTMLInputElement;
    this._value = target.value;

    // Clear validation error eagerly when user provides valid input
    if (this._validationMessage && this._value.trim()) {
      this._validationMessage = null;
      this.updateRender();
    }

    this.emit('input', { value: this._value });
  };

  private handleBlur = (_e: Event): void => {
    _e.stopPropagation();
    this._touched = true;
    this._validate();
    this.updateRender();
    this.emit('blur', { value: this._value });
  };

  private updateRender(): void {
    const effectiveError = this._error || this._validationMessage;
    const hasError = !!effectiveError;
    const labelColorClass = hasError ? 'text-error-500' : 'text-neutral-700';
    const inputBorderClass = hasError
      ? 'border-error-500 bg-error-50/50 text-error-900 focus:border-error-500 focus:ring-2 focus:ring-error-500'
      : 'border-neutral-300 bg-white text-neutral-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500';

    // Preserve focus and cursor position across re-render
    const prevInput = this.$('input') as HTMLInputElement | null;
    const hadFocus = prevInput && this.ownerDocument.activeElement === prevInput;
    const selStart = hadFocus ? prevInput!.selectionStart : null;
    const selEnd = hadFocus ? prevInput!.selectionEnd : null;

    this.render(`
      <div class="group">
        ${
          this._label
            ? `
          <label class="block text-sm font-medium ${labelColorClass} mb-1.5 ml-1" for="${this._inputId}">
            ${this.esc(this._label)}${this._required ? '<span class="ml-1 text-error-500" aria-hidden="true">*</span>' : ''}
          </label>
        `
            : ''
        }
        <div class="relative">
          <input
            id="${this._inputId}"
            type="${this._type}"
            class="block w-full rounded-lg border ${inputBorderClass} shadow-sm h-12 ${hasError ? 'pl-4 pr-10' : 'px-4'} transition-all focus:outline-none placeholder:text-neutral-400 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:cursor-not-allowed"
            placeholder="${this.esc(this._placeholder)}"
            value="${this.esc(this._value)}"
            ${this._required ? 'required aria-required="true"' : ''}
            ${this._disabled ? 'disabled aria-disabled="true"' : ''}
            ${hasError ? `aria-invalid="true" aria-describedby="${this._inputId}-error"` : ''}
            ${this._helperText && !hasError ? `aria-describedby="${this._inputId}-helper"` : ''}
          />
          ${
            hasError
              ? `
            <div class="absolute inset-y-0 right-3 flex items-center pointer-events-none text-error-500">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          `
              : ''
          }
        </div>
        ${
          hasError
            ? `
          <p id="${this._inputId}-error" class="mt-1.5 ml-1 text-xs text-error-500 flex items-center gap-1" role="alert">
            ${this.esc(effectiveError!)}
          </p>
        `
            : ''
        }
        ${
          this._helperText && !hasError
            ? `
          <p id="${this._inputId}-helper" class="mt-1.5 ml-1 text-xs text-neutral-500">
            ${this.esc(this._helperText!)}
          </p>
        `
            : ''
        }
      </div>
    `);

    // Attach event listeners after render
    const input = this.$('input');
    if (input) {
      input.addEventListener('input', this.handleInput);
      input.addEventListener('blur', this.handleBlur);

      // Restore focus and cursor position if input was focused before re-render
      if (hadFocus) {
        (input as HTMLInputElement).focus();
        (input as HTMLInputElement).setSelectionRange(selStart, selEnd);
      }
    }
  }
}

customElements.define('dt-input', DtInput);
